import { CartAddEvent, CartUpdateEvent, buildCartSnapshot } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { getSessionProductHandles } from '@theme/azuli-session-products';

/**
 * Persona fallback when session has no product views yet.
 */
const PERSONA_RANKS = {
  'acne-teen': ['mblue-barrier-whipped-tallow-cream', 'golen-hour-glow-up'],
  'eczema-mom': ['golen-hour-glow-up', 'mblue-barrier-whipped-tallow-cream'],
  'ingredient-skeptic': ['mblue-barrier-whipped-tallow-cream', 'golen-hour-glow-up'],
  'deal-hunter': ['golen-hour-glow-up', 'mblue-barrier-whipped-tallow-cream'],
  'gift-buyer': ['golen-hour-glow-up', 'mblue-barrier-whipped-tallow-cream'],
};

const PERSONA_POOL = Object.keys(PERSONA_RANKS);
const PERSONA_STORAGE_KEY = 'azuli_cart_persona';

/**
 * @typedef {{ id: number, handle: string, title: string, variantId: number, price: number, image: string, url: string }} CatalogProduct
 * @typedef {{ items: Array<{ product_id: number, final_line_price: number, quantity?: number }>, total_price: number }} CartLike
 */

class CartSuggestionComponent extends HTMLElement {
  /** @type {CatalogProduct[]} */
  #catalog = [];

  /** @type {string} */
  #persona = '';

  connectedCallback() {
    this.#catalog = this.#readCatalog();
    this.#persona = this.#resolvePersona();

    this.#bindUi();
    document.addEventListener(CartUpdateEvent.eventName, this.#onCartChange);
    document.addEventListener(CartAddEvent.eventName, this.#onCartChange);

    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(CartUpdateEvent.eventName, this.#onCartChange);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartChange);
  }

  #onCartChange = () => {
    requestAnimationFrame(() => this.#refresh());
  };

  #readCatalog() {
    const el = document.getElementById('cart-suggestion-catalog');
    if (!el?.textContent) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #resolvePersona() {
    try {
      let persona = localStorage.getItem(PERSONA_STORAGE_KEY);
      if (!persona || !PERSONA_POOL.includes(persona)) {
        persona = PERSONA_POOL[Math.floor(Math.random() * PERSONA_POOL.length)];
        localStorage.setItem(PERSONA_STORAGE_KEY, persona);
      }
      return persona;
    } catch {
      return PERSONA_POOL[0];
    }
  }

  #bindUi() {
    const btn = this.querySelector('.cart-suggestion__add');
    btn?.addEventListener('click', () => this.#handleAdd());
  }

  async #refresh() {
    const threshold = Number(this.dataset.shippingThreshold) || 0;
    if (!threshold || this.#catalog.length === 0) {
      this.hidden = true;
      return;
    }

    let cart = null;
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
    } catch {
      this.hidden = true;
      return;
    }

    if (!cart?.items?.length) {
      this.hidden = true;
      return;
    }

    const cartTotal = this.#cartTotal(cart);
    if (cartTotal >= threshold) {
      this.hidden = true;
      return;
    }

    const product = this.#pickProduct(cart, cartTotal, threshold);
    if (!product) {
      this.hidden = true;
      return;
    }

    this.#render(product);
    this.hidden = false;
  }

  /** @param {CartLike} cart */
  #cartTotal(cart) {
    return cart.items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
  }

  /**
   * Always cross-sell a catalog product not already in cart.
   * Rank: session-viewed handles not in cart first, else persona rank.
   *
   * @param {CartLike} cart
   * @param {number} cartTotal
   * @param {number} threshold
   * @returns {CatalogProduct | null}
   */
  #pickProduct(cart, cartTotal, threshold) {
    const catalogHandles = new Set(this.#catalog.map((p) => p.handle));
    const viewed = getSessionProductHandles().filter((h) => catalogHandles.has(h));
    const inCartIds = new Set(cart.items.map((i) => i.product_id));

    const notInCart = this.#catalog.filter((p) => !inCartIds.has(p.id));
    if (!notInCart.length) return null;

    const viewedNotInCart = viewed.filter((h) => notInCart.some((p) => p.handle === h));
    const rankHandles =
      viewedNotInCart.length > 0
        ? viewedNotInCart
        : PERSONA_RANKS[this.#persona] || [];

    return this.#bestByScore(notInCart, cartTotal, threshold, rankHandles);
  }

  /**
   * @param {CatalogProduct[]} candidates
   * @param {number} cartTotal
   * @param {number} threshold
   * @param {string[]} rankHandles
   */
  #bestByScore(candidates, cartTotal, threshold, rankHandles) {
    if (!candidates.length) return null;

    const score = (/** @type {CatalogProduct} */ p) => {
      const rankIdx = rankHandles.indexOf(p.handle);
      const rankScore = rankIdx === -1 ? 999 : rankIdx;
      const crosses = cartTotal + p.price >= threshold ? 0 : 1;
      return [crosses, rankScore, p.price];
    };

    return [...candidates].sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      for (let i = 0; i < 3; i++) {
        if (sa[i] !== sb[i]) return sa[i] - sb[i];
      }
      return 0;
    })[0];
  }

  /** @param {CatalogProduct} product */
  #render(product) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const imgEl = this.querySelector('.cart-suggestion__image');
    const titleEl = this.querySelector('.cart-suggestion__title');
    const priceEl = this.querySelector('.cart-suggestion__price');
    const btn = this.querySelector('.cart-suggestion__add');

    if (imgEl instanceof HTMLImageElement) {
      imgEl.src = product.image;
      imgEl.alt = product.title;
    }
    if (titleEl) titleEl.textContent = product.title;
    if (priceEl) priceEl.textContent = this.#formatMoney(moneyFormat, product.price);
    if (btn instanceof HTMLButtonElement) {
      btn.dataset.variantId = String(product.variantId);
      btn.disabled = false;
      btn.textContent = btn.dataset.defaultLabel || 'Add';
    }

    this.dataset.activeStrategy = 'cross-sell';
    this.dataset.activePersona = this.#persona;
    this.dataset.activeHandle = product.handle;
  }

  async #handleAdd() {
    const btn = this.querySelector('.cart-suggestion__add');
    if (!(btn instanceof HTMLButtonElement)) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const defaultLabel = btn.textContent || 'Add';
    btn.dataset.defaultLabel = defaultLabel;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.root + 'cart/add.js', {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const responseData = await response.json();
      if (responseData.status) {
        throw new Error(responseData.message || 'Add failed');
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-suggestion-component',
          itemCount: 1,
          sections: responseData.sections,
          cartSnapshot: buildCartSnapshot(responseData),
        })
      );
    } catch (error) {
      console.error('Cart suggestion add failed:', error);
      btn.disabled = false;
      btn.textContent = defaultLabel;
    }
  }

  #formatMoney(format, cents) {
    const amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }
}

if (!customElements.get('cart-suggestion-component')) {
  customElements.define('cart-suggestion-component', CartSuggestionComponent);
}
